/**
 * PropertyDetail — the permanent property record screen.
 * Route: /properties/:propertyId
 *
 * Phase 1 corrections (2026-06-18):
 *   A. Single combined "lat, long" coords box (mirrors StaffTechnicalPanel).
 *   B. Address auto-fetched via Nominatim reverse geocoding.
 *   C. Identity card restructured: P##### chip + linked project + address.
 *   D. Site Location panel mirrors StaffTechnicalPanel's Site & Orientation UI.
 *   E. Floors card — shows note to link project (Phase 2 floor plan reuse deferred).
 *   F. Duplicate property button (admin only).
 *
 * Layout: two columns on desktop (left = main record, right = sidebar),
 * single column on mobile. Matches the two-column ClientDetail pattern.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import {
  addOccupant,
  archiveProperty,
  createProperty,
  listenAllProperties,
  removeOccupant,
  updatePropertyIdentity,
  uploadPropertyNorthScreenshot,
  savePropertyNorthScreenshot,
} from '../lib/propertyStore';
import {
  PROJECT_TYPE_LABELS,
  type LiveProperty,
  type OccupantRole,
  type ProjectType,
  type PropertyNorthScreenshot,
  type PropertySiteLocation,
} from '../lib/propertyModel';
import {
  getClient,
  listenClients,
  type LiveClient,
} from '../lib/projectStore';
import {
  buildSiteLocation,
  hasValidCoords,
  isShortGoogleLink,
  parseGoogleMapsUrl,
  parseCoordPair,
  formatCoordPair,
  mapLaunchUrls,
} from '../lib/siteLocation';
import { reverseGeocode } from '../lib/geocode';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import PropertyDesignFloors from '../components/PropertyDesignFloors';
import IntakeLinkSendBar from '../components/IntakeLinkSendBar';

// ─── Helpers ──────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

const field: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink)',
  margin: '0 0 4px',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '14px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

const OCCUPANT_ROLE_LABELS: Record<OccupantRole, string> = {
  owner: 'Owner',
  tenant: 'Tenant',
  occupant: 'Occupant',
  business: 'Business',
};

// ─── Linked project info (resolved from engagementRefs) ──────────────────

interface LinkedProjectInfo {
  projectId: string;      // opaque doc id / route target, e.g. "VP675"
  vp: string;             // human VP number (the editable label — display only)
  clientNo: string;
  clientName: string;
}

/** One-shot read of a project doc to get clientId + clientNo + clientName. */
async function resolveLinkedProject(projectId: string): Promise<LinkedProjectInfo | null> {
  try {
    const snap = await getDoc(doc(db, 'projects', projectId));
    if (!snap.exists()) return null;
    const d = snap.data() as Record<string, unknown>;
    const clientNo = String(d.clientNo ?? d.clientId ?? '');
    const clientName = String(d.clientName ?? '');
    if (!clientNo) return null;
    // Human VP number is the `vp` field (design 2026-07-17). Fall back to the
    // doc id only if `vp` is somehow absent (pre-backfill). No extra read — the
    // doc is already in hand here.
    const vp = (typeof d.vp === 'string' && d.vp.trim()) ? d.vp.trim() : projectId;
    // Try to get a fresher name from the clients collection
    const client = await getClient(clientNo);
    return {
      projectId,
      vp,
      clientNo,
      clientName: client?.name ?? clientName,
    };
  } catch {
    return null;
  }
}

// ─── Identity card ────────────────────────────────────────────────────────

function IdentityCard({
  prop,
  linkedProject,
  onSaved,
}: {
  prop: LiveProperty;
  linkedProject: LinkedProjectInfo | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const { user } = useAuth();

  const [address, setAddress] = useState(prop.address ?? '');
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAddress(prop.address ?? '');
    setDirty(false);
  }, [prop.address]);

  async function handleSave() {
    const trimmed = address.trim();
    setBusy(true);
    try {
      await updatePropertyIdentity(
        prop.propertyId,
        { address: trimmed || undefined },
        user?.uid ?? 'unknown',
      );
      toast('Address updated.', { ok: true });
      setDirty(false);
      onSaved();
    } catch (err) {
      console.error('updatePropertyIdentity failed', err);
      toast('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Auto-fetch address from stored coordinates
  async function autoFetchAddress() {
    const lat = prop.siteLocation?.lat;
    const lng = prop.siteLocation?.lng;
    if (lat == null || lng == null) return;
    setFetchingAddress(true);
    try {
      const fetched = await reverseGeocode(lat, lng);
      if (fetched) {
        setAddress(fetched);
        setDirty(true);
      }
    } finally {
      setFetchingAddress(false);
    }
  }

  return (
    <Box title="Identity">
      {/* P##### chip — top, large, monospace */}
      <div
        style={{
          fontFamily: 'monospace',
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: 'var(--ink)',
          marginBottom: 10,
        }}
      >
        {prop.propertyId}
      </div>

      {/* Linked project line — VP# · C##### · Client Name */}
      {linkedProject ? (
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
          <Link
            to={`/project/${linkedProject.projectId}`}
            style={{ color: 'var(--link)', marginRight: 6 }}
          >
            {linkedProject.vp}
          </Link>
          <span style={{ color: 'var(--ink-mute)' }}>·</span>{' '}
          <Link
            to={`/contacts/${linkedProject.clientNo}`}
            style={{ color: 'var(--link)', margin: '0 6px' }}
          >
            {linkedProject.clientNo}
          </Link>
          <span style={{ color: 'var(--ink-mute)' }}>·</span>{' '}
          <span style={{ marginLeft: 6 }}>{linkedProject.clientName}</span>
        </div>
      ) : prop.engagementRefs.length > 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 10 }}>
          {prop.engagementRefs.length} project(s) linked — loading client info…
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 10 }}>
          No project linked.
        </div>
      )}

      {/* Address — auto-fetched or manual */}
      <Lbl>
        Address{' '}
        <span style={{ fontSize: 10, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
      </Lbl>
      <div style={{ position: 'relative' }}>
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
          value={address}
          onChange={(e) => { setAddress(e.target.value); setDirty(true); }}
          placeholder="Full property address (auto-fetched or enter manually)"
          disabled={fetchingAddress}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {prop.siteLocation?.lat != null && !address && (
          <Btn3 intent="ghost" size="sm" onClick={autoFetchAddress} disabled={fetchingAddress}>
            {fetchingAddress ? 'Fetching…' : 'Auto-fetch from coordinates'}
          </Btn3>
        )}
        {dirty && (
          <Btn3 intent="grn" size="sm" onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save address'}
          </Btn3>
        )}
      </div>
    </Box>
  );
}

// ─── Site Location card — mirrors StaffTechnicalPanel's Site & orientation ─

function SiteLocationCard({ prop }: { prop: LiveProperty }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const sl = prop.siteLocation;

  // ── Google Maps URL (Box 2) ──
  const [mapsUrl, setMapsUrl] = useState(sl?.mapsUrl ?? '');

  // ── Combined coords field (ONE box — decimal or DMS) ──
  // Seed with the stored rawCoords if present, else canonical "lat, lng" string.
  const storedCanonical = sl?.lat != null && sl?.lng != null
    ? formatCoordPair(sl.lat, sl.lng)
    : '';
  const [coords, setCoords] = useState(sl?.rawCoords ?? storedCanonical);

  // ── North bearing ──
  const [northRotation, setNorthRotation] = useState(
    prop.northRotation !== undefined ? String(prop.northRotation) : '',
  );
  const [savingNorth, setSavingNorth] = useState(false);
  const [savedNorth, setSavedNorth] = useState<number | null>(
    prop.northRotation ?? null,
  );

  // ── Property type ──
  const [projectType, setProjectType] = useState<ProjectType | ''>(
    prop.projectType ?? '',
  );

  // ── North screenshot (real upload to properties/{pid}/north_screenshot.ext) ──
  const shotFileRef = useRef<HTMLInputElement | null>(null);
  const [northShot, setNorthShot] = useState<PropertyNorthScreenshot | null>(
    prop.northScreenshot ?? null,
  );
  const [uploadingShot, setUploadingShot] = useState(false);

  const [busy, setBusy] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);

  // Sync when Firestore prop changes
  useEffect(() => {
    const s = prop.siteLocation;
    setMapsUrl(s?.mapsUrl ?? '');
    const canon = s?.lat != null && s?.lng != null ? formatCoordPair(s.lat, s.lng) : '';
    setCoords(s?.rawCoords ?? canon);
    setNorthRotation(prop.northRotation !== undefined ? String(prop.northRotation) : '');
    setSavedNorth(prop.northRotation ?? null);
    setProjectType(prop.projectType ?? '');
    setNorthShot(prop.northScreenshot ?? null);
  }, [prop.siteLocation, prop.northRotation, prop.projectType, prop.northScreenshot]);

  // Mirror coords extracted from mapsUrl INTO the coords field (same pattern
  // as StaffTechnicalPanel: link → field mirror, single source of truth).
  const parsedFromUrl = useMemo(
    () => (mapsUrl.trim() ? parseGoogleMapsUrl(mapsUrl) : null),
    [mapsUrl],
  );
  const shortLink = useMemo(() => isShortGoogleLink(mapsUrl), [mapsUrl]);
  const linkExtract = useMemo(
    () => (parsedFromUrl ? formatCoordPair(parsedFromUrl.lat, parsedFromUrl.lon) : ''),
    [parsedFromUrl],
  );
  useEffect(() => {
    if (linkExtract && linkExtract !== coords) setCoords(linkExtract);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkExtract]);

  // Build live site from the combined field (same as StaffTechnicalPanel).
  const liveSite = useMemo(
    () => buildSiteLocation({ coordPair: coords, googleUrl: mapsUrl }),
    [coords, mapsUrl],
  );
  const coordsInvalid = useMemo(
    () => coords.trim() !== '' && parseCoordPair(coords) == null,
    [coords],
  );
  const launch = hasValidCoords(liveSite)
    ? mapLaunchUrls(liveSite.lat, liveSite.lon)
    : null;

  // ── Auto-fetch address when coords change ──
  async function autoFetchAddress() {
    if (!liveSite.lat || !liveSite.lon) return;
    setFetchingAddress(true);
    try {
      const fetched = await reverseGeocode(liveSite.lat, liveSite.lon);
      if (fetched) {
        await updatePropertyIdentity(
          prop.propertyId,
          { address: fetched },
          user?.uid ?? 'unknown',
        );
        toast('Address auto-fetched from coordinates.', { ok: true });
      }
    } catch {
      toast('Could not fetch address.');
    } finally {
      setFetchingAddress(false);
    }
  }

  // ── Save site location ──
  async function handleSaveSite() {
    if (!liveSite.lat && !liveSite.lon && !mapsUrl.trim()) {
      toast('Enter a Maps URL or coordinates before saving.');
      return;
    }
    setBusy(true);
    try {
      // Build the property siteLocation shape (different from staffIntake SiteLocation)
      const newSl: PropertySiteLocation = {};
      if (mapsUrl.trim()) newSl.mapsUrl = mapsUrl.trim();
      if (liveSite.lat != null && liveSite.lon != null) {
        newSl.lat = liveSite.lat;
        newSl.lng = liveSite.lon;
        newSl.coordFormat = 'decimal';
        if (coords.trim()) newSl.rawCoords = coords.trim();
      }
      await updatePropertyIdentity(
        prop.propertyId,
        { siteLocation: newSl, projectType: projectType || undefined },
        user?.uid ?? 'unknown',
      );
      // Mirror canonical value back into field (normalises DMS/messy input)
      if (liveSite.lat != null && liveSite.lon != null) {
        setCoords(formatCoordPair(liveSite.lat, liveSite.lon));
      }
      setMapsUrl(liveSite.google_maps_url);
      toast('Site location saved.', { ok: true });
    } catch (err) {
      console.error('save siteLocation failed', err);
      toast('Could not save location.');
    } finally {
      setBusy(false);
    }
  }

  // ── Save north bearing ──
  async function handleSaveNorth() {
    const val = parseFloat(northRotation);
    if (Number.isNaN(val) || val < 0 || val > 360) {
      toast('Enter a number between 0 and 360.');
      return;
    }
    setSavingNorth(true);
    try {
      await updatePropertyIdentity(
        prop.propertyId,
        { northRotation: val },
        user?.uid ?? 'unknown',
      );
      setSavedNorth(val);
      toast(`North bearing saved (${val}°).`, { ok: true });
    } catch {
      toast('Could not save north bearing.');
    } finally {
      setSavingNorth(false);
    }
  }

  const northVal = northRotation.trim() !== '' ? parseFloat(northRotation) : null;
  const northInvalid =
    northRotation.trim() !== '' &&
    (northVal == null || Number.isNaN(northVal) || northVal < 0 || northVal > 360);

  return (
    <Box
      title="Site & orientation"
      note="Where the site is + which way it faces — mirrors project technical panel"
    >
      {/* ─ Section heading ─ */}
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', margin: '2px 0 4px', letterSpacing: 0.2 }}>
        Site location
      </div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
        Paste a Google link (or coordinates) — then open the site on a map in one click.
      </p>

      {/* (1) Google Maps URL */}
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Paste a Google Maps link</label>
        <input
          style={field}
          value={mapsUrl}
          onChange={(e) => setMapsUrl(e.target.value)}
          placeholder="Paste the full maps.google.com link"
        />
        {parsedFromUrl && (
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            Extracted {parsedFromUrl.lat}, {parsedFromUrl.lon} (from the {parsedFromUrl.via} in the link) — filled into Coordinates below.
          </p>
        )}
        {!parsedFromUrl && shortLink && (
          <p style={{ fontSize: 11.5, color: 'var(--crit, #dc2626)', marginTop: 4 }}>
            This is a shortened link — open it, then paste the FULL link from the address bar (or enter the coordinates below).
          </p>
        )}
        {!parsedFromUrl && !shortLink && mapsUrl.trim() !== '' && (
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            No coordinates found in that link — enter them in the field below.
          </p>
        )}
      </div>

      {/* (2) Combined coordinates field */}
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Coordinates (latitude, longitude)</label>
        <input
          style={field}
          value={coords}
          onChange={(e) => setCoords(e.target.value)}
          placeholder="Paste lat, long from Google (e.g. 21.2345, 81.6789 or DMS)"
        />
        {coordsInvalid ? (
          <p style={{ fontSize: 11.5, color: 'var(--crit, #dc2626)', marginTop: 4 }}>
            Couldn't read those coordinates. Use "lat, long" — decimal (e.g. 22.7196, 75.8577) or DMS (e.g. 22°43'10.6"N 75°51'27.7"E).
          </p>
        ) : parsedFromUrl ? (
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            Filled from the pasted link — select + copy it, or type over it to override.
          </p>
        ) : (
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
            Decimal or DMS. Use minus for south/west.
          </p>
        )}
      </div>

      {/* (3) Open-in buttons — Google Maps + Google Earth */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '4px 0 4px' }}>
        {launch ? (
          <>
            <a href={launch.google} target="_blank" rel="noopener noreferrer">
              <Btn3 intent="ink" size="sm">Google Maps</Btn3>
            </a>
            <a href={launch.earth} target="_blank" rel="noopener noreferrer">
              <Btn3 intent="ink" size="sm">Google Earth</Btn3>
            </a>
            <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              {liveSite.lat}, {liveSite.lon}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
            Add coordinates above to enable the map buttons.
          </span>
        )}
      </div>

      {/* Auto-fetch address */}
      {launch && !prop.address && (
        <div style={{ marginTop: 6 }}>
          <Btn3 intent="ghost" size="sm" onClick={autoFetchAddress} disabled={fetchingAddress}>
            {fetchingAddress ? 'Fetching address…' : 'Auto-fetch address from coordinates'}
          </Btn3>
          <span style={{ fontSize: 11, color: 'var(--ink-mute)', marginLeft: 8 }}>
            Fills the address field via Nominatim (free, no API key).
          </span>
        </div>
      )}

      {/* Property type (moved here from Identity per Phase 1 spec) */}
      <div style={{ marginTop: 14 }}>
        <label style={lbl}>Property type</label>
        <select
          style={{ ...field, maxWidth: 240 }}
          value={projectType}
          onChange={(e) => setProjectType(e.target.value as ProjectType | '')}
        >
          <option value="">— not set —</option>
          {(Object.entries(PROJECT_TYPE_LABELS) as [ProjectType, string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Save site location + property type */}
      <div style={{ marginTop: 12 }}>
        <Btn3 intent="grn" size="sm" onClick={handleSaveSite} disabled={busy}>
          {busy ? 'Saving…' : 'Save site location'}
        </Btn3>
      </div>

      {/* ── Divider ── */}
      <div style={{ height: 1, background: 'var(--rule)', margin: '18px 0 14px' }} />

      {/* True north section */}
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', margin: '2px 0 8px', letterSpacing: 0.2 }}>
        True north
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 180px' }}>
          <label style={lbl}>True-north bearing (°)</label>
          <input
            style={field}
            value={northRotation}
            onChange={(e) => setNorthRotation(e.target.value)}
            placeholder="e.g. 347"
            inputMode="decimal"
          />
        </div>
        <Btn3
          intent="grn"
          size="sm"
          onClick={handleSaveNorth}
          disabled={savingNorth || northInvalid || northRotation.trim() === ''}
        >
          {savingNorth ? 'Saving…' : 'Save north'}
        </Btn3>
        {savedNorth != null && (
          <Tag kind="val" title="Stored on the property record">
            saved · {savedNorth}°
          </Tag>
        )}
      </div>
      {northInvalid && (
        <p style={{ fontSize: 12, color: 'var(--crit, #dc2626)', marginTop: 6 }}>
          Enter a number between 0 and 360.
        </p>
      )}
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 8 }}>
        0° = grid aligned to true north. Confirm from the surveyed site plan.
      </p>

      {/* North screenshot — real upload to properties/{pid}/north_screenshot.ext */}
      <div style={{ marginTop: 14 }}>
        <label style={lbl}>Google Earth north screenshot</label>
        <input
          ref={shotFileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            e.target.value = '';
            setUploadingShot(true);
            try {
              const shot = await uploadPropertyNorthScreenshot(
                prop.propertyId,
                f,
                user?.uid ?? 'unknown',
              );
              await savePropertyNorthScreenshot(prop.propertyId, shot);
              setNorthShot(shot);
              toast('North screenshot uploaded.', { ok: true });
            } catch (err) {
              toast(`Upload failed: ${(err as Error).message}`);
            } finally {
              setUploadingShot(false);
            }
          }}
        />
        {northShot?.downloadUrl ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <a href={northShot.downloadUrl} target="_blank" rel="noopener noreferrer" title="Open the full screenshot">
              <img
                src={northShot.downloadUrl}
                alt="True-north Google Earth screenshot"
                style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 'var(--r-sm)', border: '1px solid var(--rule-strong)', display: 'block' }}
              />
            </a>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn3 intent="ghost" size="sm" onClick={() => shotFileRef.current?.click()} disabled={uploadingShot}>
                {uploadingShot ? 'Uploading…' : 'Replace'}
              </Btn3>
              <Btn3
                intent="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    await savePropertyNorthScreenshot(prop.propertyId, null);
                    setNorthShot(null);
                    toast('North screenshot removed.', { ok: true });
                  } catch {
                    toast('Could not remove screenshot.');
                  }
                }}
              >
                Remove
              </Btn3>
            </div>
          </div>
        ) : (
          <Btn3 intent="ink" size="sm" onClick={() => shotFileRef.current?.click()} disabled={uploadingShot}>
            {uploadingShot ? 'Uploading…' : 'Upload Google Earth screenshot'}
          </Btn3>
        )}
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6 }}>
          Optional. Stored on the property record — used in the report.
        </p>
      </div>
    </Box>
  );
}

// ─── Floors card ──────────────────────────────────────────────────────────
// Phase 1: basic floor list on the property doc.
// Phase 2 (future): read project intake floors + reuse StaffTechnicalPanel floor components.

// ─── Occupants card ───────────────────────────────────────────────────────

function OccupantsCard({
  prop,
  contacts,
}: {
  prop: LiveProperty;
  contacts: LiveClient[];
}) {
  const { toast } = useToast();
  const [addingOccupant, setAddingOccupant] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedRole, setSelectedRole] = useState<OccupantRole>('owner');
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);
  // Keyboard navigation for the contact search dropdown
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const existingIds = new Set((prop.occupants ?? []).map((o) => o.contactId));

  const matchingContacts = contacts
    .filter(
      (c) =>
        !existingIds.has(c.clientNo) &&
        (search.trim() === '' ||
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.clientNo.toLowerCase().includes(search.toLowerCase())),
    )
    .slice(0, 8);

  // Reset highlight whenever the search query changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  function selectContact(clientNo: string) {
    setSelectedContactId(clientNo);
    setSearch('');
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (matchingContacts.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, matchingContacts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0) {
        e.preventDefault();
        selectContact(matchingContacts[highlightedIndex].clientNo);
      }
    } else if (e.key === 'Escape') {
      setSearch('');
      setSelectedContactId('');
    }
  }

  async function handleAddOccupant() {
    if (!selectedContactId) {
      toast('Select a contact first.');
      return;
    }
    setBusy(true);
    try {
      await addOccupant(prop.propertyId, {
        contactId: selectedContactId,
        role: selectedRole,
        relationship: relationship.trim() || undefined,
      });
      toast('Occupant added.', { ok: true });
      setAddingOccupant(false);
      setSearch('');
      setSelectedContactId('');
      setRelationship('');
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('addOccupant failed', err);
      toast('Could not add occupant.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveOccupant(contactId: string) {
    if (!window.confirm('Remove this occupant from the property?')) return;
    try {
      await removeOccupant(prop.propertyId, contactId);
      toast('Occupant removed.', { ok: true });
    } catch {
      toast('Could not remove occupant.');
    }
  }

  const occupants = prop.occupants ?? [];

  function resolveContactName(contactId: string): string {
    const c = contacts.find((c) => c.clientNo === contactId);
    return c ? c.name : contactId;
  }

  return (
    <Box title="Occupants" allowOverflow>
      {occupants.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '6px 0' }}>
          No occupants linked yet.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {occupants.map((o) => (
            <div
              key={o.contactId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px',
                background: 'var(--bg)',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--rule)',
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Link to={`/contacts/${o.contactId}`} style={{ fontWeight: 600, fontSize: 13 }}>
                  {resolveContactName(o.contactId)}
                </Link>
                <Tag kind="val">{OCCUPANT_ROLE_LABELS[o.role] ?? o.role}</Tag>
                {o.relationship && (
                  <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                    {o.relationship}
                  </span>
                )}
              </div>
              <Btn3 intent="ghost" size="sm" onClick={() => handleRemoveOccupant(o.contactId)}>
                Remove
              </Btn3>
            </div>
          ))}
        </div>
      )}

      {addingOccupant ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {/* Contact search with keyboard-navigable dropdown */}
          <div style={{ position: 'relative' }}>
            <input
              className="ipt"
              type="search"
              placeholder="Search contacts by name or ID…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedContactId(''); }}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
              style={{ width: '100%', minHeight: 38, fontSize: 14, padding: '6px 10px', boxSizing: 'border-box' }}
            />
            {/* Custom dropdown — shown while typing (search non-empty and no contact selected yet) */}
            {search.trim() !== '' && !selectedContactId && matchingContacts.length > 0 && (
              <div
                role="listbox"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  background: 'var(--surface)',
                  border: '1px solid var(--rule-strong)',
                  borderRadius: 'var(--r-sm)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                  marginTop: 2,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {matchingContacts.map((c, i) => (
                  <div
                    key={c.clientNo}
                    role="option"
                    aria-selected={i === highlightedIndex}
                    onMouseDown={(e) => { e.preventDefault(); selectContact(c.clientNo); }}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    style={{
                      padding: '7px 10px',
                      fontSize: 13,
                      cursor: 'pointer',
                      background: i === highlightedIndex ? '#eef4fb' : 'transparent',
                      borderBottom: i < matchingContacts.length - 1 ? '1px solid var(--rule)' : 'none',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-mute)' }}>
                      {c.clientNo}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirmation chip — shows the selected contact */}
          {selectedContactId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg)', borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {contacts.find((c) => c.clientNo === selectedContactId)?.name ?? selectedContactId}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>{selectedContactId}</span>
              <Btn3 intent="ghost" size="sm" onClick={() => { setSelectedContactId(''); setSearch(''); }}>
                Change
              </Btn3>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              style={{ ...inputStyle, flex: 1 }}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as OccupantRole)}
            >
              {(Object.entries(OCCUPANT_ROLE_LABELS) as [OccupantRole, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <input
              style={{ ...inputStyle, flex: 2 }}
              type="text"
              placeholder='Relationship (e.g. "primary occupant") — optional'
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn3 intent="grn" size="sm" onClick={handleAddOccupant} disabled={busy || !selectedContactId}>
              {busy ? 'Adding…' : 'Add occupant'}
            </Btn3>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => { setAddingOccupant(false); setSearch(''); setSelectedContactId(''); setHighlightedIndex(-1); }}
            >
              Cancel
            </Btn3>
          </div>
        </div>
      ) : (
        <Btn3 intent="ink" size="sm" onClick={() => setAddingOccupant(true)}>
          + Add occupant
        </Btn3>
      )}
    </Box>
  );
}

// ─── Projects sidebar card ────────────────────────────────────────────────

function ProjectsSidebar({ prop }: { prop: LiveProperty }) {
  const refs = prop.engagementRefs;
  return (
    <Box title="Projects">
      {refs.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '4px 0' }}>
          No projects linked.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {refs.map((r) => (
            <div
              key={r.projectId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '5px 8px',
                background: 'var(--bg)',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--rule)',
              }}
            >
              <Link to={`/project/${r.projectId}`} style={{ fontWeight: 600, fontSize: 13 }}>
                {r.projectId}
              </Link>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                {r.label && (
                  <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                    {r.label}
                  </span>
                )}
                <Tag kind={r.role === 'primary' ? 'val' : 'default'}>
                  {r.role === 'primary' ? 'Primary' : 'Candidate'}
                </Tag>
              </div>
            </div>
          ))}
        </div>
      )}
    </Box>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export default function PropertyDetail() {
  const { propertyId = '' } = useParams();
  const [searchParams] = useSearchParams();
  // Deep-link intent from the project L1A "Grids & charts" rollup: when staff
  // click "Open grid on Property page →", we arrive with ?openGrid=1 and the
  // Design & floors block scrolls to a gridable floor and auto-opens the tracer.
  const autoOpenGrid = searchParams.get('openGrid') === '1';
  // The EXACT project we came from (e.g. "VP678"), passed by the project page's
  // grid-rollup deep-link as &from=. Used to render a precise "← Back to VP678".
  // Falls back to linkedProject (engagementRefs[0]) for direct visits.
  const fromProject = searchParams.get('from') ?? '';
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const { toast } = useToast();

  const [prop, setProp] = useState<LiveProperty | null | undefined>(undefined); // undefined = loading
  const [contacts, setContacts] = useState<LiveClient[]>([]);
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // M5-S4: "Send Intake Form for this Property" → IntakeLinkSendBar scoped to it.
  const [showSend, setShowSend] = useState(false);

  // Resolved linked project info (client name + VP# + C#####)
  const [linkedProject, setLinkedProject] = useState<LinkedProjectInfo | null>(null);

  // Phase 2: clientId + intakeTier for StaffTechnicalPanel (floors/grid section)
  const [linkedClientId, setLinkedClientId] = useState<string | null>(null);
  const [linkedIntakeTier, setLinkedIntakeTier] = useState<string | null>(null);
  // AD-030 — the project this property's intake belongs to (VP###), so the
  // embedded StaffTechnicalPanel reads/writes the PROJECT-scoped intake doc.
  const [linkedProjectId, setLinkedProjectId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = listenAllProperties((all) => {
      const found = all.find((p) => p.propertyId === propertyId);
      setProp(found ?? null);
    });
    return unsub;
  }, [propertyId]);

  // Resolve first linked project once we have the property
  useEffect(() => {
    if (!prop || prop.engagementRefs.length === 0) {
      setLinkedProject(null);
      return;
    }
    const firstRef = prop.engagementRefs[0];
    resolveLinkedProject(firstRef.projectId).then(setLinkedProject);
  }, [prop]);

  // Resolve linkedClientId + linkedIntakeTier for the StaffTechnicalPanel (Phase 2)
  useEffect(() => {
    if (!prop || prop.engagementRefs.length === 0) {
      setLinkedClientId(null);
      setLinkedIntakeTier(null);
      setLinkedProjectId(null);
      return;
    }
    const projectId = prop.engagementRefs[0].projectId;
    setLinkedProjectId(projectId || null);
    // clientId for StaffTechnicalPanel = the project's clientNo (C##### / VP### key)
    // The project doc stores clientNo (e.g. "C00123") which is what loadPropertiesIntake uses.
    getDoc(doc(db, 'projects', projectId)).then((snap) => {
      if (!snap.exists()) {
        setLinkedClientId(null);
        setLinkedIntakeTier(null);
        return;
      }
      const d = snap.data() as Record<string, unknown>;
      const clientNo = String(d.clientNo ?? d.clientId ?? '').trim();
      setLinkedClientId(clientNo || null);
      // Derive intakeTier: strip the display suffix from tier (e.g. "L2B · Alignment Path…" → "L2B")
      const rawTier = String(d.tierCode ?? d.tier ?? '').split(/\s*[·•]/)[0].trim().toUpperCase();
      // Only "L1A" and "L2B" are valid deliverable tiers with intake docs
      const validTiers = ['L1A', 'L2B'];
      const bareTier = validTiers.includes(rawTier) ? rawTier : null;
      setLinkedIntakeTier(bareTier ? `${bareTier}_R1` : null);
    }).catch(() => {
      setLinkedClientId(null);
      setLinkedIntakeTier(null);
    });
  }, [prop]);

  // Contacts — needed for occupants panel
  useEffect(() => {
    const unsub = listenClients(setContacts);
    return unsub;
  }, []);

  async function handleArchive() {
    if (!window.confirm(`Archive property ${propertyId}? It will be hidden from the default list but all data is preserved.`)) return;
    setArchiving(true);
    try {
      await archiveProperty(propertyId);
      toast(`${propertyId} archived.`, { ok: true });
      navigate('/properties');
    } catch (err) {
      console.error('archiveProperty failed', err);
      toast('Could not archive.');
      setArchiving(false);
    }
  }

  async function handleDuplicate() {
    if (!prop) return;
    setDuplicating(true);
    try {
      const newPid = await createProperty(
        {
          address: prop.address,
          siteLocation: prop.siteLocation,
          projectType: prop.projectType,
          northRotation: prop.northRotation,
          addressComps: prop.addressComps,
        },
        user?.uid ?? 'unknown',
      );
      toast(`Duplicated as ${newPid}. Link it to a project to begin work.`, { ok: true });
      navigate(`/properties/${newPid}`);
    } catch (err) {
      console.error('duplicate property failed', err);
      toast('Could not duplicate property.');
    } finally {
      setDuplicating(false);
    }
  }

  // actor for PropertyDesignFloors (grid + plan writes).
  const actor = { uid: user?.uid ?? null, name: user?.displayName ?? user?.email ?? 'Staff' };

  if (prop === undefined) {
    return (
      <div style={{ padding: 24, color: 'var(--ink-mute)', fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  if (!prop) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyNote title="Property not found">
          <p style={{ marginTop: 8 }}>
            <Link to="/properties">Back to Properties</Link>
          </p>
        </EmptyNote>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Back link */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
        {(() => {
          // Prefer the explicit ?from= (the exact project the deep-link came
          // from); otherwise fall back to the resolved linked project.
          const backProject = fromProject || linkedProject?.projectId || '';
          if (!backProject) return null;
          return (
            <Link
              to={`/project/${backProject}`}
              style={{ fontSize: 12, color: 'var(--accent, #2f5d8a)', fontWeight: 600 }}
            >
              ← Back to {backProject}
            </Link>
          );
        })()}
        <Link to="/properties" style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          ← All properties
        </Link>
      </div>

      {/* Two-column layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 280px',
          gap: 20,
          alignItems: 'start',
        }}
        className="detail-grid"
      >
        {/* ── Left / Main ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <IdentityCard prop={prop} linkedProject={linkedProject} onSaved={() => {}} />
          <SiteLocationCard prop={prop} />
          {/* AD-038 — ONE top-down "Design & floors" block (replaces the old
               separate Floors-&-Grid + Design-Concepts sections). One-design vs
               Multiple-options → Concept chips → Version chips → Floors list. */}
          <PropertyDesignFloors
            prop={prop}
            actor={actor}
            clientId={linkedClientId ?? undefined}
            projectId={linkedProjectId ?? undefined}
            tier={linkedIntakeTier ?? undefined}
            autoOpenGrid={autoOpenGrid}
          />
          <OccupantsCard prop={prop} contacts={contacts} />
        </div>

        {/* ── Right / Sidebar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Property ID chip with copy */}
          <Card flat>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: 'var(--ink)', letterSpacing: '0.04em' }}>
                {prop.propertyId}
              </span>
              <Btn3
                intent="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(prop.propertyId).then(
                    () => toast(`Copied ${prop.propertyId}`, { ok: true }),
                    () => toast('Could not copy.'),
                  );
                }}
                title="Copy property ID"
              >
                Copy
              </Btn3>
            </div>
          </Card>

          <ProjectsSidebar prop={prop} />

          {/* M5-S4 — Send the intake form for THIS property. Resolves the
              property's linked project + that project's client, then opens the
              IntakeLinkSendBar fixed to this propertyId (no picker). Needs a
              linked project to know which client + project the link belongs to. */}
          <Card flat>
            {linkedProject ? (
              <Btn3 intent="grn" size="sm" onClick={() => setShowSend(true)}>
                Send Intake Form for this Property
              </Btn3>
            ) : (
              <Btn3
                intent="ghost"
                size="sm"
                disabled
                title="Link this property to a project first — the intake link is sent to that project's client."
              >
                Send Intake Form for this Property
              </Btn3>
            )}
            <p style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
              {linkedProject
                ? `Sends ${linkedProject.clientName || linkedProject.clientNo} a link to fill the intake for this property.`
                : 'Available once this property is linked to a project.'}
            </p>
          </Card>

          {/* Duplicate — admin only */}
          {role === 'admin' && (
            <Card flat>
              <Btn3 intent="ghost" size="sm" onClick={handleDuplicate} disabled={duplicating}>
                {duplicating ? 'Duplicating…' : 'Duplicate property'}
              </Btn3>
              <p style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
                Creates a copy with the same address and site location. No floors or project links copied.
              </p>
            </Card>
          )}

          {/* Archive — admin only */}
          {role === 'admin' && !prop.archived && (
            <Card flat>
              <Btn3 intent="ghost" size="sm" onClick={handleArchive} disabled={archiving} style={{ color: '#b91c1c' }}>
                {archiving ? 'Archiving…' : 'Archive property'}
              </Btn3>
              <p style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
                Hides this property from the default list. Data is never deleted.
              </p>
            </Card>
          )}

          {prop.archived && (
            <Card flat>
              <Tag kind="warn">Archived</Tag>
              <p style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
                This property is archived. Contact an admin to restore it.
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* M5-S4 — Send intake form modal, fixed to this property. */}
      {showSend && linkedProject && (
        <IntakeLinkSendBar
          cid={linkedProject.clientNo}
          pid={linkedProject.projectId}
          propertyId={prop.propertyId}
          onClose={() => setShowSend(false)}
        />
      )}
    </div>
  );
}
